<template>
  <section
    v-if="data"
    class="w-full overflow-hidden"
    :class="[backgroundColorClass, sectionOuterSpacingClass(data), { 'py-10 lg:py-20': data?.backgroundColor !== 'transparent' }]"
    :style="backgroundColorStyle"
  >
    <UiHeading
      v-if="data?.heading"
      :pin="data?.heading?.pin"
      :title="data?.heading?.title"
      :description="data?.heading?.description"
      :button="data?.heading?.button"
      :color="data?.heading?.color"
      :position="data?.heading?.position"
      v-aos="animate && 'zoom-out'"
    />
    <div 
      v-for="(item, index) in data.items" 
      :key="index"
      :class="containerClass"
    >
        <div
          class="relative flex align-items-center flex-col lg:flex-row"
          :class="[{ 'lg:flex-row-reverse': item.mediaPosition === 'right' }]"
        >
          <div
            v-aos="animate && storyAnimation(item.mediaPosition, 'media')"
            class="w-full lg:w-1/2 h-max overflow-hidden bg-white border border-gray-50 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]"
            :class="mediaWrapperClass(item.mediaPosition)"
          >
            <AtomsImage
              v-if="item?.media?.mime?.includes('image')"
              class="w-full flex"
              :class="imageClass(item?.mediaSize)"
              :media="item?.media"
              sizeHint="half"
              :alt="item?.media?.alternativeText ?? item.title ?? item?.media?.hash ?? item?.media?.name"
            />
          </div>
          <div
            class="w-full lg:w-1/2 flex justify-center"
            v-aos="animate && storyAnimation(item.mediaPosition, 'content')"
          >
            <div
              v-if="item.pin || item.title || item.description"
              class="h-full flex flex-col justify-center rich-text"
              :class="contentWrapperClass"
            >
              <span v-if="item.pin" class="text-[12px] xl:text-[13px] font-bold mb-2 uppercase tracking-widest" :class="isFirstOrSecondBg ? 'text-white/70' : 'text-gray-400'">
                {{ item.pin }}
              </span>
              <h3 v-if="item.title" class="text-xl lg:text-2xl xl:text-3xl font-black mb-3 lg:mb-5" :class="itemTextClass" v-html="item.title"></h3>
              <div v-if="item.description" class="text-[14px] xl:text-[15px] font-normal leading-relaxed story-description" :class="isFirstOrSecondBg ? 'text-white/90' : 'text-gray-600'" v-html="item.description"></div>
              <AtomsButton
                v-if="item?.button?.enabled"
                :label="item?.button?.label"
                :link="item?.button?.link"
                :target="item?.button?.target"
                :color="item?.button?.color"
                :icon="item?.button?.icon"
                :iconPosition="item?.button?.iconPosition"
                :size="isMobile ? 'small' : item?.button?.size"
                :enabled="item.enabled"
                :disabled="item.disabled"
                class="mt-6"
              />
            </div>
          </div>
        </div>
      </div>
  </section>
</template>
<script setup>
import { computed } from "vue";
import { useIsMobile, useIsMobile768, getAspectClass, getBrandBgClass } from "~/composables/utils";
import { sectionOuterSpacingClass } from "~/composables/sectionSpacing";

const props = defineProps({
  data: {
    type: Object,
  },
  animate: { type: Boolean, default: false },
});

const { isMobile } = useIsMobile();
const { isMobile768 } = useIsMobile768();

const backgroundColorClass = computed(() => {
  const bg = props.data?.backgroundColor;
  const brandClass = getBrandBgClass(bg);
  return brandClass || '';
});

const backgroundColorStyle = computed(() => {
  const bg = props.data?.backgroundColor;
  if (bg && bg.startsWith("#")) {
    return { backgroundColor: bg };
  }
  return {};
});

const isFirstOrSecondBg = computed(() => {
  const bg = props.data?.backgroundColor;
  return bg === 'first' || bg === 'second';
});

const storyAnimation = (mediaPosition, side) => {
  if (isMobile768.value) return "fade-up";
  const isRight = mediaPosition === "right";
  if (side === "media") {
    return isRight ? "slide-left" : "slide-right";
  }
  return isRight ? "slide-right" : "slide-left";
};

const containerClass = computed(() => {
  return props.data?.containerFull ? 'w-full px-0' : 'container';
});

const mediaWrapperClass = (mediaPosition) => {
  if (!props.data?.containerFull) {
    return 'rounded-2xl lg:rounded-3xl';
  }
  const isRight = mediaPosition === 'right';
  return isRight ? 'rounded-2xl lg:rounded-l-3xl lg:rounded-r-none' : 'rounded-2xl lg:rounded-r-3xl lg:rounded-l-none';
};

const imageClass = (size) => {
  const aspect = getAspectClass(size);
  return `${aspect} object-cover`;
};

const contentWrapperClass = computed(() => {
  return props.data?.containerFull ? 'w-full py-4 lg:px-12 xl:px-0 xl:w-[550px]' : 'w-full py-4 lg:px-12 2xl:px-16';
});

const itemTextClass = computed(() => {
  const bg = props.data?.backgroundColor;
  return bg === 'first' || bg === 'second' ? 'text-white' : 'text-black';
});
</script>
